import type { Product, ProductGroup } from '../types/index.js';
import {
  buildStorefrontProductLookup,
  getMergedStorefrontProducts,
  getStorefrontGroupProducts,
} from './storefront-catalog.js';
import { isProductInStock } from './storefront-stock.js';

export interface StorefrontSearchFilterOptions {
  hideOutOfStock?: boolean;
  maxResults?: number;
}

export type StorefrontCatalogSearchItem =
  | { type: 'product'; product: Product }
  // Groups only carry `product_uniqids`; `products` holds the resolved product
  // objects (in group order) so consumers never re-resolve references themselves.
  | { type: 'group'; group: ProductGroup; products: Product[] };

export function stripHtmlFromText(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function normalizeSearchQuery(query: string): string {
  return query.trim().toLowerCase();
}

function pushSearchPart(parts: string[], value: string | null | undefined): void {
  const normalized = stripHtmlFromText(value).toLowerCase();
  if (normalized) {
    parts.push(normalized);
  }
}

export function collectProductSearchHaystack(product: Product): string[] {
  const parts: string[] = [];

  pushSearchPart(parts, product.title);
  pushSearchPart(parts, product.slug ?? undefined);
  pushSearchPart(parts, product.description);

  for (const highlight of product.product_highlights ?? []) {
    pushSearchPart(parts, highlight);
  }

  for (const variant of product.variants ?? []) {
    pushSearchPart(parts, variant.title);
  }

  for (const variant of product.price_variants ?? []) {
    pushSearchPart(parts, variant.title ?? variant.label);
  }

  return parts;
}

export function productMatchesSearchQuery(product: Product, query: string): boolean {
  const normalized = normalizeSearchQuery(query);
  if (!normalized) return false;

  return collectProductSearchHaystack(product).some((haystack) => haystack.includes(normalized));
}

export function groupMatchesSearchQuery(group: ProductGroup, query: string): boolean {
  const normalized = normalizeSearchQuery(query);
  if (!normalized) return false;

  const title = stripHtmlFromText(group.title).toLowerCase();
  const slug = stripHtmlFromText(group.slug ?? group.name ?? undefined).toLowerCase();
  const description = stripHtmlFromText(group.description).toLowerCase();

  return title.includes(normalized)
    || slug.includes(normalized)
    || description.includes(normalized);
}

export function filterProductsBySearchQuery(
  products: Product[],
  query: string,
  options?: StorefrontSearchFilterOptions,
): Product[] {
  const normalized = normalizeSearchQuery(query);
  if (!normalized) return [];

  let results = products.filter((product) => productMatchesSearchQuery(product, normalized));

  if (options?.hideOutOfStock) {
    results = results.filter((product) => isProductInStock(product));
  }

  if (options?.maxResults != null) {
    return results.slice(0, options.maxResults);
  }

  return results;
}

function groupHasVisibleProducts(
  groupProducts: Product[],
  hideOutOfStock: boolean,
): boolean {
  if (groupProducts.length === 0) return false;
  if (!hideOutOfStock) return true;
  return groupProducts.some((product) => isProductInStock(product));
}

export function searchMergedStorefrontCatalogItems(
  products: Product[],
  groups: ProductGroup[],
  query: string,
  options?: StorefrontSearchFilterOptions,
): StorefrontCatalogSearchItem[] {
  const normalized = normalizeSearchQuery(query);
  if (!normalized) return [];

  const hideOutOfStock = options?.hideOutOfStock === true;
  const coveredProductIds = new Set<string>();
  const coveredGroupIds = new Set<string>();
  const results: StorefrontCatalogSearchItem[] = [];
  // Build the uniqid lookup once per search run; groups resolve against it.
  const lookup = buildStorefrontProductLookup(products);

  for (const group of groups) {
    const groupKey = group.uniqid ?? group.id;
    if (!groupKey || coveredGroupIds.has(groupKey)) continue;
    if (!groupMatchesSearchQuery(group, normalized)) continue;

    const groupProducts = getStorefrontGroupProducts(group, lookup);
    if (!groupHasVisibleProducts(groupProducts, hideOutOfStock)) continue;

    coveredGroupIds.add(groupKey);
    for (const product of groupProducts) {
      if (product?.uniqid) coveredProductIds.add(product.uniqid);
    }
    results.push({ type: 'group', group, products: groupProducts });
  }

  const merged = getMergedStorefrontProducts(products);
  for (const product of merged) {
    if (!product?.uniqid || coveredProductIds.has(product.uniqid)) continue;
    if (!productMatchesSearchQuery(product, normalized)) continue;
    if (hideOutOfStock && !isProductInStock(product)) continue;
    coveredProductIds.add(product.uniqid);
    results.push({ type: 'product', product });
  }

  if (options?.maxResults != null) {
    return results.slice(0, options.maxResults);
  }

  return results;
}

export function searchMergedStorefrontCatalog(
  products: Product[],
  groups: ProductGroup[],
  query: string,
  options?: StorefrontSearchFilterOptions,
): Product[] {
  const items = searchMergedStorefrontCatalogItems(products, groups, query, options);
  const matchedIds = new Set<string>();
  const results: Product[] = [];

  const addProduct = (product: Product) => {
    if (!product?.uniqid || matchedIds.has(product.uniqid)) return;
    if (options?.hideOutOfStock && !isProductInStock(product)) return;
    matchedIds.add(product.uniqid);
    results.push(product);
  };

  for (const item of items) {
    if (item.type === 'product') {
      addProduct(item.product);
      continue;
    }

    for (const product of item.products) {
      addProduct(product);
    }
  }

  if (options?.maxResults != null) {
    return results.slice(0, options.maxResults);
  }

  return results;
}
